<template>
  <div class="slot-box" ref="root">
    <div class="net-work-row flex-layout" style="align-items: center">
      <div class="net-work-port" v-for="(item, index) in props.list" :key="index">
        <div class="port-item port-item-one" :style="calcStyle(item.portNum)">
          <el-popover
            placement="top"
            popper-class="sakura-child-port-popover"
            trigger="click"
            :disabled="!checkFanout(item.portNum)"
            @after-enter="initInfoHandle"
            @show="() => fanoutChange(item.portNum)"
            @hide="() => fanoutChange()"
            :visible="popoverControl[item.portNum]"
          >
            <template #default>
              <sakura-child-panel
                :list="props.fanout.filter(obj => obj.rootPortNum === item.portNum)"
                :popover="props.popover"
                :value="props.value"
                :multiple="props.multiple"
                :isEnabled="props.isEnabled"
                :isEnabledTu="props.isEnabledTu"
                @change="onChildSelectChange"
              />
            </template>

            <template #reference>
              <div @click="handlePortClick(item)" style="cursor: pointer; height: 100%;">
                <sakura-port-lan
                  @port-click="() => emit('port-click', item)"
                  @port-click-fanout="() => emit('port-click-fanout', item)"
                  @change="(num) => emit('change', num, item)"
                  :type="item.type"
                  :isEnabledTu="props.isEnabledTu && item.isEnabledTu"
                  :tu="item.tu"
                  @update:tu="val => item.tu = val"
                  :value="item.selected"
                  :port="item.portNum"
                  :popover="props.popover"
                  :isEnabled="props.isEnabled && item.isEnabled"
                  :status="item.status"
                  :borderStatus="item.borderStatus"
                  :hex="item.hex"
                  :mode="item.mode"
                  :showVendor="item.showVendor"
                  :lag="item.lag"
                  :priority="item.priority"
                  :vendor="item.detect"
                  :config="item.config"
                  :isFanout="checkFanout(item.portNum)"
                >
                  {{ item.value }}
                </sakura-port-lan>
              </div>
            </template>
          </el-popover>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, onBeforeUnmount } from 'vue'
import sakuraPortLan from './sakura-port-lan.vue'
import sakuraChildPanel from './sakura-child-panel.vue'
import { inject, defineProps, defineEmits } from 'vue'

const fanoutNum = ref(null)
const popoverControl = ref({})
const popoverTimers = ref({})
const root = ref(null)

const props = defineProps({
  popover: {
    type: Boolean,
    default: false
  },
  value: {
    type: Array,
    default: () => []
  },
  multiple: {
    type: Boolean,
    default: true
  },
  isEnabled: {
    type: Boolean,
    default: true
  },
  isEnabledTu: {
    type: Boolean,
    default: false
  },
  list: {
    type: Array,
    default: () => []
  },
  fanout: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['change', 'port-click', 'port-click-fanout'])
const network = inject('network')

const calcStyle = computed(() => (num) => {
  const obj = {}
  if (fanoutNum.value && fanoutNum.value !== num) {
    obj['filter'] = 'blur(3px)'
    obj['pointer-events'] = 'none'
  }
  return obj
})

function handlePortClick(item) {
  for (const key in popoverControl.value) {
    popoverControl.value[key] = false
  }
  if (!checkFanout(item.portNum)) {
    network.fanoutNum = null
    return
  }
  popoverControl.value[item.portNum] = true
  network.fanoutNum = item.portNum
}

const checkFanout = (port) => {
  return props.fanout?.length && !!props.fanout.find(item => item.rootPortNum === port)
}

function fanoutChange(num) {
  fanoutNum.value = num
  if (network) {
    network.fanoutNum = num ?? null
  }
}

const initInfoHandle = () => {}
function onChildSelectChange(num, obj) {
  emit('change', num, obj)
  popoverControl.value[network.fanoutNum] = true
}

function nodeInPathMatches(path, selector) {
  if (!path) return false;
  for (const node of path) {
    if (!node || node.nodeType !== 1) continue;
    try {
      if (node.matches(selector)) return true;
    } catch { /* ignore invalid node types */ }
  }
  return false;
}

function handleGlobalClick(e) {
  const target = e.target;
  const path = (e.composedPath && e.composedPath()) || (function buildPath(n) {
    const p = [];
    while (n) { p.push(n); n = n.parentNode; }
    return p;
  })(target);

  if (nodeInPathMatches(path, '.el-select, .el-select__trigger, .el-select__popper, .el-select-dropdown, .el-select .el-input')) {
    return;
  }

  if (nodeInPathMatches(path, '.manual_trunk_select-popper, .profile_select-popper')) {
    return;
  }

  if (nodeInPathMatches(path, '.sakura-child-port-popover')) {
    return;
  }

  if (nodeInPathMatches(path, '.sakura-port-panel, .page-network-container, .slot-body, .slot-box')) {
    if (network) {
      network.fanoutNum = null;
    }
    return;
  }

  if (nodeInPathMatches(path, '.sakura-port-popover')) {
    if (network) network.fanoutNum = null;
    return;
  }

  if (root.value && root.value.contains(target)) {
    return;
  }

  for (const k in popoverControl.value) popoverControl.value[k] = false;
  if (network) network.fanoutNum = null;
}
onMounted(() => {
  document.addEventListener("click", handleGlobalClick)
})
onBeforeUnmount(() => {
  document.removeEventListener("click", handleGlobalClick)
})
</script>


<style lang="scss" scoped>
@use "@/css/base.scss" as *;

.net-work-row {
  height: 50px;
}

.net-work-port {
  flex: 1;
  height: inherit;
  min-width: 50px;

  .port-item {
    height: 100%;
    line-height: 50px;
    text-align: center;
    position: relative;
  }

  .port-item-one {
    border-right: 1px solid $network-border-color;
    border-bottom: 1px solid $network-border-color;
  }

  .port-item-two {
    border-right: 1px solid $network-border-color;
  }

  .port-item port-item-one:last-child,
  .port-item port-item-two:last-child {
    border-right: none;
  }
}
</style>
